import { useCallback, useEffect, useMemo, useState, type FC, type ReactNode } from 'react'

// Types
export type Locale = 'en' | 'es'

import enTranslations from '../../locales/en.json'
import esTranslations from '../../locales/es.json'
import { I18nContext } from './i18nContext'

export interface I18nContextType {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string, params?: Record<string, string | number>) => string
  isLoading: boolean
}

const translations = {
  es: esTranslations,
  en: enTranslations,
}

function getNestedValue(obj: Record<string, unknown>, path: string): string | undefined {
  return path.split('.').reduce<unknown>((current, key) => {
    return current && typeof current === 'object' && current !== null && key in current
      ? (current as Record<string, unknown>)[key]
      : undefined
  }, obj) as string | undefined
}

interface I18nProviderProps {
  children: ReactNode
  initialLocale?: Locale
}

export const I18nProvider: FC<I18nProviderProps> = ({ children, initialLocale }) => {
  const [currentLocale, setCurrentLocale] = useState<Locale>(initialLocale || 'es')
  const [isLoading, setIsLoading] = useState(false)

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      let translation = getNestedValue(translations[currentLocale], key)

      if (translation === undefined) {
        translation = key
      }

      if (params && typeof translation === 'string') {
        Object.entries(params).forEach(([param, value]) => {
          translation = translation!.replace(new RegExp(`{{${param}}}`, 'g'), String(value))
        })
      }

      return translation
    },
    [currentLocale]
  )

  const setLocale = useCallback((newLocale: Locale) => {
    setIsLoading(true)
    setCurrentLocale(newLocale)

    try {
      localStorage.setItem('preferred-locale', newLocale)
    } catch (error) {
      console.error('Could not save locale preference to localStorage:', error)
    }

    setIsLoading(false)
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedLocale = localStorage.getItem('preferred-locale') as Locale
      const browserLocale = navigator.language.startsWith('es') ? 'es' : 'en'

      const preferredLocale = savedLocale || browserLocale || 'es'

      if (preferredLocale !== currentLocale) {
        setCurrentLocale(preferredLocale)
      }
    }
  }, [currentLocale])

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = currentLocale
    }
  }, [currentLocale])

  const value: I18nContextType = useMemo(
    () => ({
      locale: currentLocale,
      setLocale,
      t,
      isLoading,
    }),
    [currentLocale, setLocale, t, isLoading]
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}
